import { Suspense, useEffect, useState } from 'react';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import EnhancedConsentBanner from './components/EnhancedConsentBanner';
import ErrorBoundary from './components/ErrorBoundary';
import FloatingButtons from './components/FloatingButtons';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import { RecaptchaProvider } from './contexts/RecaptchaContext';
import { RouterProvider } from './contexts/RouterContext';
import { usePageTracking } from './utils/analytics';
import { initConsentManager } from './utils/initConsentManager';
import { initializeAnalytics } from './utils/initializeAnalytics';
import {
  ComponentLoadingFallback,
  LazyAbout,
  LazyBlog,
  LazyBlogPostDetail,
  LazyCaseStudies,
  LazyCaseStudyDetail,
  LazyContact,
  LazyCookiePreferences,
  LazyHome,
  LazyPlanes,
  LazySearchResults,
  LazyServiceDetail,
  LazyServices,
} from './utils/lazyComponents';
import { initPerformanceMonitoring } from './utils/performance';
import { preloadCriticalComponents } from './utils/preload';

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const options = {
      top: 0,
      left: 0,
      behavior: 'smooth' as const,
    };

    // Usar requestAnimationFrame para asegurar que el scroll se realice después de la actualización del DOM
    requestAnimationFrame(() => {
      window.scrollTo(options);
    });
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  const [hasError, setHasError] = useState(false);
  usePageTracking();

  useEffect(() => {
    let mounted = true;

    // Inicializar cada servicio de forma independiente para evitar que un error en uno afecte a los demás.
    const initServices = async () => {
      try {
        // Inicializar el gestor de consentimiento primero
        await Promise.resolve(initConsentManager()).catch((error: Error) => {
          console.error('Error al inicializar el gestor de consentimiento:', error);
        });

        // Esperar un poco antes de inicializar los demás servicios
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Inicializar servicios en paralelo
        await Promise.allSettled([
          Promise.resolve(initPerformanceMonitoring()).catch((error: Error) => {
            console.error('Error al inicializar el monitoreo de rendimiento:', error);
          }),
          Promise.resolve(preloadCriticalComponents()).catch((error: Error) => {
            console.error('Error en la precarga de componentes críticos:', error);
          }),
          Promise.resolve(initializeAnalytics()).catch((error: Error) => {
            console.error('Error al inicializar los servicios de analíticas:', error);
          }),
        ]);
      } catch (error) {
        console.error('Error crítico durante la inicialización:', error);
        if (mounted) {
          setHasError(true);
        }
      }
    };

    initServices();

    return () => {
      mounted = false;
    };
  }, []);

  // Modo de fallback si hay errores críticos
  if (hasError) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Modo de Emergencia</h1>
        <p className="text-gray-700 mb-6 text-center max-w-md">
          Estamos experimentando problemas técnicos. Nuestro equipo está trabajando para resolverlos
          lo antes posible.
        </p>
        <div className="bg-blue-50 p-6 rounded-lg shadow-md max-w-md w-full">
          <h2 className="text-xl font-semibold text-blue-800 mb-3">Contacto de Emergencia</h2>
          <p className="text-gray-600 mb-4">Puede contactarnos directamente a través de:</p>
          <ul className="list-disc pl-5 mb-4 text-gray-700">
            <li>Email: contacto@solinprimejc.com</li>
            <li>Teléfono: +56 9 XXXX XXXX</li>
          </ul>
          <button
            onClick={() => setHasError(false)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors w-full"
          >
            Intentar cargar normalmente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <ErrorBoundary>
        <RecaptchaProvider>
          <RouterProvider>
            <ScrollToTop />
            <Navbar />
            <main className="flex-1">
              <Suspense fallback={<ComponentLoadingFallback message="Cargando página..." />}>
                <Routes>
                  <Route path="/" element={<LazyHome />} />
                  <Route path="/servicios" element={<LazyServices />} />
                  <Route path="/servicios/:slug" element={<LazyServiceDetail />} />
                  <Route path="/nosotros" element={<LazyAbout />} />
                  <Route path="/contacto" element={<LazyContact />} />
                  <Route path="/planes" element={<LazyPlanes />} />
                  <Route path="/blog" element={<LazyBlog />} />
                  <Route path="/blog/:slug" element={<LazyBlogPostDetail />} />
                  <Route path="/casos-de-exito" element={<LazyCaseStudies />} />
                  <Route path="/casos-de-exito/:id" element={<LazyCaseStudyDetail />} />
                  <Route path="/buscar" element={<LazySearchResults />} />
                  <Route path="/preferencias-de-cookies" element={<LazyCookiePreferences />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
            <FloatingButtons />
            <EnhancedConsentBanner />
          </RouterProvider>
        </RecaptchaProvider>
      </ErrorBoundary>
    </div>
  );
};

export default App;
